/**
 * Message Router
 * Parses incoming WebSocket messages and routes them to the appropriate dispatcher events.
 */
import { MessageDispatcher } from './message-handler.js';
import { MessageData, MessageImageData, ButtonOption } from './types.js';
import { config } from '../config/environment.js';
import { normalizeShops } from './shop-normalizer.js';

export interface MessageRouterDeps {
  dispatcher: MessageDispatcher;
  stopReconnection: () => void;
  dispatchLoginEvent: () => void;
}

export class MessageRouter {
  constructor(private readonly deps: MessageRouterDeps) { }

  /**
   * Parses raw MessageEvent data (JSON or plain text) and routes to dispatcher.
   */
  handleRaw(event: MessageEvent): void {
    try {
      const data: MessageData = JSON.parse(event.data);
      this.route(data);
    } catch (parseError) {
      this.deps.dispatcher.notify('message', { data: event.data });
    }
  }

  /**
   * Routes a parsed MessageData to the correct dispatcher event.
   */
  private route(data: MessageData): void {
    const {
      type,
      data: messageData,
      error,
      hierarchy_buttons,
      normal_buttons,
      list_buttons,
      csat,
      recipes,
      coupons,
      stores,
      image,
    } = data;

    if (error) {
      this.deps.dispatcher.notify('error', { message: error });
      return;
    }

    switch (type) {
      case 'message':
        this.deps.dispatcher.notify('message', {
          data: typeof messageData === 'string' ? messageData : '',
          hierarchy_buttons,
          normal_buttons,
          list_buttons,
          csat,
          recipes,
          coupons,
          shops: normalizeShops(stores),
          image: this.toImage(messageData) ?? this.toImage(image),
        });
        break;
      case 'hierarchy_buttons':
        this.deps.dispatcher.notify('message', {
          data: '',
          hierarchy_buttons: this.toArray(messageData, hierarchy_buttons),
        });
        break;
      case 'normal_buttons':
        this.deps.dispatcher.notify('message', {
          data: '',
          normal_buttons: this.toArray(messageData, normal_buttons),
        });
        break;
      case 'list_buttons':
        this.deps.dispatcher.notify('message', {
          data: '',
          list_buttons: this.toArray(messageData, list_buttons),
        });
        break;
      case 'csat':
        this.deps.dispatcher.notify('message', {
          data: '',
          csat: this.toCsatButtons(messageData),
        });
        break;
      case 'recipes':
        this.deps.dispatcher.notify('message', {
          data: '',
          recipes: this.toArray(messageData, recipes),
        });
        break;
      case 'coupons':
        this.deps.dispatcher.notify('message', {
          data: '',
          coupons: this.toArray(messageData, coupons),
        });
        break;
      case 'stores':
        this.deps.dispatcher.notify('message', {
          data: '',
          shops: normalizeShops(Array.isArray(messageData) ? messageData : stores),
        });
        break;
      case 'image':
        this.deps.dispatcher.notify('message', {
          data: '',
          image: this.toImage(messageData) ?? this.toImage(image),
        });
        break;
      case 'finished':
        this.deps.dispatcher.notify('finished', {});
        this.deps.dispatcher.notify('typing', { isTyping: false });
        break;
      case 'finished_all':
        this.deps.dispatcher.notify('finished_all', {});
        this.deps.dispatcher.notify('typing', { isTyping: false });
        break;
      case 'typing':
        this.deps.dispatcher.notify('typing', { isTyping: data.isTyping });
        break;
      case 'error':
        // 401/403 = auth error, don't retry. Other errors = let it retry.
        if (typeof messageData === 'string' && (messageData.includes('401') || messageData.includes('403'))) {
          this.deps.stopReconnection();
        }
        this.deps.dispatcher.notify('error', { message: config.MESSAGES.GENERIC_ERROR_MESSAGE });
        break;
      case 'error_authentication':
        // Agent reports authentication error — dispatch login event so the host app can re-authenticate
        this.deps.dispatchLoginEvent();
        this.deps.stopReconnection();
        break;
      default:
    }
  }

  private toArray<T>(primary: unknown, fallback?: T[]): T[] | undefined {
    if (Array.isArray(primary)) {
      return primary as T[];
    }
    if (Array.isArray(fallback)) {
      return fallback;
    }
    return undefined;
  }

  private toImage(value: unknown): { source: string; link?: string | null } | undefined {
    if (!value || typeof value !== 'object') {
      return undefined;
    }

    const candidate = value as MessageImageData & { source?: string };
    let source: string | undefined;
    if (typeof candidate.url === 'string') {
      source = candidate.url;
    } else if (typeof candidate.source === 'string') {
      source = candidate.source;
    } else {
      source = undefined;
    }

    if (!source) {
      return undefined;
    }

    return {
      source,
      link: typeof candidate.link === 'string' || candidate.link === null ? candidate.link : undefined,
    };
  }

  /**
   * Transforms CSAT rating structure to ButtonOption array.
   * Input: { type: 'rating', choices: [1, 2, 3, ..., 10] }
   * Output: [ { label: '1', value: '1' }, { label: '2', value: '2' }, ... ]
   */
  private toCsatButtons(value: unknown): ButtonOption[] | undefined {
    if (!value || typeof value !== 'object') {
      return undefined;
    }

    const candidate = value as { type?: string; choices?: unknown[] };
    if (candidate.type !== 'rating' || !Array.isArray(candidate.choices)) {
      return undefined;
    }

    return candidate.choices
      .filter((choice): choice is number => typeof choice === 'number')
      .map((choice) => ({
        label: String(choice),
        value: String(choice),
      }));
  }
}
